import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Dropdown, Dimmer, Loader, Message, Progress, Divider, Input, Button, Checkbox, Icon, Modal } from "semantic-ui-react";
import { Link } from "react-router-dom";
import format from "date-fns/format";
import {
  SG_GET_SURVEY_STRUCTURES,
  SG_GET_EMPLOYEE_ORG_CATEGORIES,
  UPDATE_EMPLOYEE,
  SG_ADD_EMPLOYEE_LOG,
} from "constants/actions";

const MassRemove = () => {
  const dispatch = useDispatch();

  const {
    get_auth,
    get_employees,
    get_survey_structure,
    get_employee_org_category,
    categoriesFetched,
  } = useSelector(
    (state) => ({
      get_auth: state.auth,
      get_employees: state.employees,
      get_survey_structure: state.surveystructure.survey_structure,
      get_employee_org_category: state.employee_category?.employee_org_category,
      categoriesFetched: state.employee_category?.fetched,
    }),
    shallowEqual
  );

  console.log(get_employee_org_category,get_employees);

  const [loading, setLoading] = useState(true);
  const [timeoutReached, setTimeoutReached] = useState(false);

  const [selectedSubfactors, setSelectedSubfactors] = useState([]); // array of subfactor ids
  const [filteredMembers, setFilteredMembers] = useState([]); // full member objects
  const [selectedIds, setSelectedIds] = useState([]); // selected member ids to archive
  const [reason, setReason] = useState("");
  const [filterDate, setFilterDate] = useState(""); // date filter (before this date)
  const [loginFilter, setLoginFilter] = useState("all"); // 'all' | 'with_login' | 'without_login'
  const [surveyFilter, setSurveyFilter] = useState("all"); // 'all' | 'with_survey' | 'without_survey'
  const [actionType, setActionType] = useState("archive"); // 'archive' | 'disable_survey' | 'enable_survey'
  // Email include/exclude filter state
  const [emailFilterMode, setEmailFilterMode] = useState(null); // 'include' | 'exclude' | null
  const [emailFilterList, setEmailFilterList] = useState([]); // normalized emails (lowercased)

  const [archiving, setArchiving] = useState(false);
  const [progressIndex, setProgressIndex] = useState(0); // how many processed
  const [perItemStatus, setPerItemStatus] = useState({}); // id -> 'queued' | 'processing' | 'done'
  const [showConfirm, setShowConfirm] = useState(false);
  
  // Email selection modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailList, setEmailList] = useState("");
  const [emailMatches, setEmailMatches] = useState([]);

  // Kickoff data loads similar to Members list
  useEffect(() => {
    if (get_auth?.organization_id) {
      if (!get_employee_org_category?.[0]) {
        dispatch({
          type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
          payload: `organization=${get_auth?.organization_id}`,
        });
      }
      if (!get_survey_structure?.[0]) {
        dispatch({
          type: SG_GET_SURVEY_STRUCTURES,
          payload: `organization=${get_auth?.organization_id}`,
        });
      }
    }
  }, [dispatch, get_auth, get_employee_org_category, get_survey_structure]);

  // Basic loading gate with timeout similar to list
  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeoutReached(true);
      setLoading(false);
    }, 30000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (
      get_employees?.employees &&
      get_survey_structure?.[0] &&
      (get_employee_org_category?.length > 0 || categoriesFetched)
    ) {
      setLoading(false);
    }
  }, [get_employees, get_survey_structure, get_employee_org_category, categoriesFetched]);

  // Build subfactor options from survey structure (primary category subfactors)
  const subfactorOptions = useMemo(() => {
    const options = [];
    const structure = get_survey_structure?.[0];
    if (!structure?.categories) return options;

    structure.categories.forEach((cat) => {
      // Subfactors primarily live under primary category options
      if (cat?.options?.length > 0) {
        cat.options.forEach((op) => {
          if (op?.subfactors?.length > 0) {
            op.subfactors.forEach((sf) => {
              options.push({ key: `${cat.id}-${op.id}-${sf.id}`, text: sf.name, value: sf.id });
            });
          }
        });
      }
    });
    // Make unique by id in case shared names
    const seen = new Set();
    return options.filter((o) => {
      if (seen.has(o.value)) return false;
      seen.add(o.value);
      return true;
    });
  }, [get_survey_structure]);

  // Set of subfactor ids as strings (ensure we treat ids as UIDs)
  const allSubfactorIds = useMemo(() => {
    const ids = new Set();
    const structure = get_survey_structure?.[0];
    if (!structure?.categories) return ids;
    structure.categories.forEach((cat) => {
      if (cat?.options?.length > 0) {
        cat.options.forEach((op) => {
          if (op?.subfactors?.length > 0) {
            op.subfactors.forEach((sf) => {
              ids.add(String(sf.id));
            });
          }
        });
      }
    });
    return ids;
  }, [get_survey_structure]);

  // Memoized set for email filter
  const emailFilterSet = useMemo(() => new Set(emailFilterList), [emailFilterList]);

  // Helper: find categories object for a member id
  const findMemberCategories = (memberId) => {
    if (!get_employee_org_category) return undefined;
    return (
      get_employee_org_category.find((c) => Number(c?.employee) === Number(memberId)) ||
      get_employee_org_category.find((c) => Number(c?.employee_id) === Number(memberId))
    );
  };

  // Compute filtered members when selections change
  useEffect(() => {
    if (!selectedSubfactors?.length) {
      setFilteredMembers([]);
      setSelectedIds([]);
      setPerItemStatus({});
      return;
    }
    const employees = (get_employees?.employees || []).filter(
      (e) => Number(e.status) !== 99 && Number(e.status) !== 98 && Number(e.status) !== 108
    );
    // normalize selection as strings
    const selectedIdSet = new Set(selectedSubfactors.map((v) => String(v)));
    // filter out any selections that aren't known subfactors (defensive)
    const effectiveSelected = new Set(
      Array.from(selectedIdSet).filter((id) => allSubfactorIds.has(id))
    );
    let nextFiltered = employees.filter((member) => {
      const cats = findMemberCategories(member.id);
      if (!cats?.category) return false;
      // Only consider primary priority where subfactors live
      const primaryCats = cats.category?.filter((c) => c?.priority === "primary") || [];
      // Compare strictly by UID strings in value_id
      const memberValueIds = new Set(
        primaryCats
          .map((c) => (c?.value_id != null ? String(c.value_id) : undefined))
          .filter(Boolean)
      );

      // match if any selected subfactor uid is present in member value_ids
      for (const sid of effectiveSelected) {
        if (memberValueIds.has(sid)) return true;
      }
      return false;
    });

    // Apply login filter
    if (loginFilter === "with_login") {
      nextFiltered = nextFiltered.filter((m) => !!m.enable_login);
    } else if (loginFilter === "without_login") {
      nextFiltered = nextFiltered.filter((m) => !m.enable_login);
    }

    // Apply survey filter (survey_status === 1 means they receive surveys)
    if (surveyFilter === "with_survey") {
      nextFiltered = nextFiltered.filter((m) => Number(m.survey_status) === 1);
    } else if (surveyFilter === "without_survey") {
      nextFiltered = nextFiltered.filter((m) => Number(m.survey_status) !== 1);
    }

    // Apply date filter (created_at before filterDate)
    if (filterDate) {
      const filterDateObj = new Date(filterDate);
      nextFiltered = nextFiltered.filter((m) => {
        if (!m.created_at) return false;
        const createdDate = new Date(m.created_at);
        return createdDate < filterDateObj;
      });
    }

    // Apply email include/exclude filter
    if (emailFilterMode && emailFilterList.length > 0) {
      nextFiltered = nextFiltered.filter((m) => {
        const em = (m.email || "").toLowerCase();
        const inList = emailFilterSet.has(em);
        return emailFilterMode === "include" ? inList : !inList;
      });
    }

    setFilteredMembers(nextFiltered);
    // Do not auto-select when an email filter is active
    if (emailFilterMode && emailFilterList.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(nextFiltered.map((m) => m.id));
    }
    // initialize status map
    const initStatus = {};
    nextFiltered.forEach((m) => (initStatus[m.id] = "queued"));
    setPerItemStatus(initStatus);
  }, [selectedSubfactors, get_employees, get_employee_org_category, loginFilter, surveyFilter, filterDate, emailFilterMode, emailFilterList, emailFilterSet]);

  // Toggle a member from selection
  const toggleSelected = (memberId) => {
    setSelectedIds((prev) => {
      if (prev.includes(memberId)) {
        const next = prev.filter((id) => id !== memberId);
        return next;
      }
      return [...prev, memberId];
    });
  };

  const selectAll = () => setSelectedIds(filteredMembers.map((m) => m.id));
  const clearAll = () => setSelectedIds([]);
  
  const selectAllWithLogin = () => {
    const withLogin = filteredMembers.filter((m) => !!m.enable_login).map((m) => m.id);
    setSelectedIds(withLogin);
  };
  
  const selectAllWithoutLogin = () => {
    const withoutLogin = filteredMembers.filter((m) => !m.enable_login).map((m) => m.id);
    setSelectedIds(withoutLogin);
  };

  const selectAllWithSurvey = () => {
    const withSurvey = filteredMembers.filter((m) => Number(m.survey_status) === 1).map((m) => m.id);
    setSelectedIds(withSurvey);
  };
  
  const selectAllWithoutSurvey = () => {
    const withoutSurvey = filteredMembers.filter((m) => Number(m.survey_status) !== 1).map((m) => m.id);
    setSelectedIds(withoutSurvey);
  };

  // Parse CSV email list and find exact matches
  const parseEmailList = (emailText) => {
    if (!emailText.trim()) {
      setEmailMatches([]);
      return;
    }

    // Split by commas, semicolons, or newlines and clean up
    const emails = emailText
      .split(/[,;\n\r]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email && email.includes('@'));

    // Find exact matches in all employees (not just filtered)
    const employees = get_employees?.employees || [];
    const matches = emails.map(email => {
      const member = employees.find(emp => 
        emp.email && emp.email.toLowerCase() === email
      );
      return {
        email,
        member,
        found: !!member
      };
    });

    setEmailMatches(matches);
  };

  // Apply email selection to current filtered members
  const applyEmailSelection = () => {
    const matchedIds = emailMatches
      .filter(match => match.found && match.member)
      .map(match => match.member.id);

    // Only select members that are in the current filtered list
    const validIds = matchedIds.filter(id => 
      filteredMembers.some(member => member.id === id)
    );

    setSelectedIds(validIds);
    setShowEmailModal(false);
    setEmailList("");
    setEmailMatches([]);
  };

  // Apply non-matching selection to current filtered members
  const applyEmailNonSelection = () => {
    if (!emailMatches.length) return;
    const pastedSet = new Set(emailMatches.map((m) => m.email));
    const nonMatchedIds = filteredMembers
      .filter((member) => !pastedSet.has((member.email || "").toLowerCase()))
      .map((m) => m.id);

    setSelectedIds(nonMatchedIds);
    setShowEmailModal(false);
    setEmailList("");
    setEmailMatches([]);
  };

  // Apply email filter include/exclude (does not change selection)
  const applyEmailFilterInclude = () => {
    const pastedSet = new Set(emailMatches.map((m) => m.email));
    setEmailFilterList(Array.from(pastedSet));
    setEmailFilterMode("include");
    setShowEmailModal(false);
    setEmailList("");
    setEmailMatches([]);
  };

  const applyEmailFilterExclude = () => {
    const pastedSet = new Set(emailMatches.map((m) => m.email));
    setEmailFilterList(Array.from(pastedSet));
    setEmailFilterMode("exclude");
    setShowEmailModal(false);
    setEmailList("");
    setEmailMatches([]);
  };

  const clearEmailFilter = () => {
    setEmailFilterMode(null);
    setEmailFilterList([]);
  };

  // Derived counts for matched/non-matched within current filtered list
  const pastedEmailSet = React.useMemo(() => new Set(emailMatches.map((m) => m.email)), [emailMatches]);
  const filteredMatchCount = React.useMemo(
    () => filteredMembers.filter((m) => pastedEmailSet.has((m.email || "").toLowerCase())).length,
    [filteredMembers, pastedEmailSet]
  );
  const filteredNonMatchCount = React.useMemo(
    () => filteredMembers.length - filteredMatchCount,
    [filteredMembers, filteredMatchCount]
  );

  // Archive or disable survey sequentially
  const isCancelledRef = useRef(false);
  const startArchive = async () => {
    isCancelledRef.current = false;
    setArchiving(true);
    setProgressIndex(0);
    const ids = [...selectedIds];
    
    for (let i = 0; i < ids.length; i++) {
      if (isCancelledRef.current) break;
      const id = ids[i];
      setPerItemStatus((m) => ({ ...m, [id]: "processing" }));
      
      if (actionType === "archive") {
        // Dispatch archive (status 99) and add log
        dispatch({
          type: UPDATE_EMPLOYEE,
          payload: { id, status: 99 },
        });
        dispatch({
          type: SG_ADD_EMPLOYEE_LOG,
          payload: {
            employee: id,
            type_of_log: 2,
            description: { Archived: reason || "Mass archive" },
          },
        });
      } else if (actionType === "disable_survey") {
        // Disable survey participation (survey_status: 2 means disabled)
        dispatch({
          type: UPDATE_EMPLOYEE,
          payload: { id, survey_status: 2 },
        });
        dispatch({
          type: SG_ADD_EMPLOYEE_LOG,
          payload: {
            employee: id,
            type_of_log: 2,
            description: { "Survey Disabled": reason || "Mass survey disable" },
          },
        });
      } else if (actionType === "enable_survey") {
        // Enable survey participation (survey_status: 1 means enabled)
        dispatch({
          type: UPDATE_EMPLOYEE,
          payload: { id, survey_status: 1 },
        });
        dispatch({
          type: SG_ADD_EMPLOYEE_LOG,
          payload: {
            employee: id,
            type_of_log: 2,
            description: { "Survey Enabled": reason || "Mass survey enable" },
          },
        });
      }
      
      // naive wait to allow saga to process; in real use we'd listen to updated id
      // keep UI responsive with a short delay
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 350));
      setPerItemStatus((m) => ({ ...m, [id]: "done" }));
      setProgressIndex((p) => p + 1);
    }
    setArchiving(false);
  };

  const cancelArchive = () => {
    isCancelledRef.current = true;
    setArchiving(false);
  };

  const total = selectedIds.length;
  const percent = total > 0 ? Math.round((progressIndex / total) * 100) : 0;

  return (
    <Dimmer.Dimmable blurring dimmed={loading}>
      <TopTitle>
        <Title>Mass Operations</Title>
        <Sub>Filter by subfactors, adjust selection, perform bulk operations sequentially</Sub>
      </TopTitle>

      <Container>
        <Section>
          <Row>
            <RowLabel>Action Type</RowLabel>
            <RowControl>
              <FilterButtonGroup>
                <FilterButton 
                  data-active={actionType === "archive"} 
                  onClick={() => setActionType("archive")}
                >
                  Archive Members
                </FilterButton>
                <FilterButton 
                  data-active={actionType === "enable_survey"} 
                  onClick={() => setActionType("enable_survey")}
                >
                  Enable Survey
                </FilterButton>
                <FilterButton 
                  data-active={actionType === "disable_survey"} 
                  onClick={() => setActionType("disable_survey")}
                >
                  Disable Survey
                </FilterButton>
              </FilterButtonGroup>
            </RowControl>
          </Row>

          <Row>
            <RowLabel>Subfactors</RowLabel>
            <RowControl>
              <Dropdown
                placeholder="Select subfactors"
                fluid
                multiple
                search
                selection
                options={subfactorOptions}
                value={selectedSubfactors}
                onChange={(e, t) => setSelectedSubfactors(t.value)}
                style={{ borderRadius: 10, minWidth: 300 }}
              />
            </RowControl>
          </Row>

          <Row>
            <RowLabel>Login Filter</RowLabel>
            <RowControl>
              <FilterButtonGroup>
                <FilterButton 
                  data-active={loginFilter === "all"} 
                  onClick={() => setLoginFilter("all")}
                >
                  All
                </FilterButton>
                <FilterButton 
                  data-active={loginFilter === "with_login"} 
                  onClick={() => setLoginFilter("with_login")}
                >
                  With Login
                </FilterButton>
                <FilterButton 
                  data-active={loginFilter === "without_login"} 
                  onClick={() => setLoginFilter("without_login")}
                >
                  Without Login
                </FilterButton>
              </FilterButtonGroup>
            </RowControl>
          </Row>

          <Row>
            <RowLabel>Survey Filter</RowLabel>
            <RowControl>
              <FilterButtonGroup>
                <FilterButton 
                  data-active={surveyFilter === "all"} 
                  onClick={() => setSurveyFilter("all")}
                >
                  All
                </FilterButton>
                <FilterButton 
                  data-active={surveyFilter === "with_survey"} 
                  onClick={() => setSurveyFilter("with_survey")}
                >
                  Receive Survey
                </FilterButton>
                <FilterButton 
                  data-active={surveyFilter === "without_survey"} 
                  onClick={() => setSurveyFilter("without_survey")}
                >
                  No Survey
                </FilterButton>
              </FilterButtonGroup>
            </RowControl>
          </Row>

          <Row>
            <RowLabel>Added Before</RowLabel>
            <RowControl>
              <Input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                style={{ width: 250 }}
                placeholder="Filter by date added"
              />
              {filterDate && (
                <Button 
                  basic 
                  size="small" 
                  onClick={() => setFilterDate("")}
                  style={{ marginLeft: 10 }}
                >
                  Clear Date
                </Button>
              )}
            </RowControl>
          </Row>

          <Row>
            <RowLabel>Reason</RowLabel>
            <RowControl>
              <Input
                placeholder={
                  actionType === "archive" 
                    ? "Archive reason (optional)" 
                    : actionType === "enable_survey"
                    ? "Enable survey reason (optional)"
                    : "Disable survey reason (optional)"
                }
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{ width: 400 }}
              />
            </RowControl>
          </Row>
        </Section>

        <Divider />

        <Section>
          <ListHeader>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Count>{selectedIds.length}</Count>
              <CountSub>selected of {filteredMembers.length} filtered</CountSub>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              <MiniButton onClick={selectAll} disabled={filteredMembers.length === 0}>Select all</MiniButton>
              <MiniButton onClick={selectAllWithLogin} disabled={filteredMembers.length === 0}>Select w/ Login</MiniButton>
              <MiniButton onClick={selectAllWithoutLogin} disabled={filteredMembers.length === 0}>Select w/o Login</MiniButton>
              <MiniButton onClick={selectAllWithSurvey} disabled={filteredMembers.length === 0}>Select w/ Survey</MiniButton>
              <MiniButton onClick={selectAllWithoutSurvey} disabled={filteredMembers.length === 0}>Select w/o Survey</MiniButton>
              <MiniButton onClick={() => setShowEmailModal(true)} disabled={filteredMembers.length === 0}>Select by Email</MiniButton>
            <MiniButton onClick={clearEmailFilter} disabled={!emailFilterMode}>Clear Email Filter</MiniButton>
              <MiniButton onClick={clearAll} disabled={selectedIds.length === 0}>Clear</MiniButton>
            </div>
          </ListHeader>

          <MemberList>
            {filteredMembers.map((m) => {
              const checked = selectedIds.includes(m.id);
              const status = perItemStatus[m.id] || "queued";
              const addedDate = m.created_at ? format(new Date(m.created_at), "MMM dd, yyyy") : "N/A";
              return (
                <MemberRow key={m.id} data-selected={checked}>
                  <RowLeft>
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleSelected(m.id)}
                      style={{ marginRight: 10 }}
                    />
                    <MemberInfo>
                      <MemberName>
                        <ProfileLink to={`/core/members/edit/${m.id}`}>
                          {m.first_name} {m.last_name}
                        </ProfileLink>
                      </MemberName>
                      <MemberEmail>{m.email}</MemberEmail>
                    </MemberInfo>
                    <TagsGroup>
                      <MiniTag data-active={Number(m.survey_status) === 1}>Survey</MiniTag>
                      <MiniTag data-active={!!m.enable_login}>Login</MiniTag>
                    </TagsGroup>
                    <AddedDate>
                      <Icon name="calendar outline" size="small" style={{ marginRight: 4 }} />
                      {addedDate}
                    </AddedDate>
                  </RowLeft>
                  <RowRight>
                    {archiving ? (
                      <StatusPill data-status={status}>
                        {status === "processing" && <Icon name="spinner" loading />}
                        {status === "queued" && <Icon name="clock outline" />}
                        {status === "done" && <Icon name="check" />}
                        <span style={{ marginLeft: 6 }}>{status}</span>
                      </StatusPill>
                    ) : (
                      <RemoveLink onClick={() => toggleSelected(m.id)}>
                        {checked ? "Remove" : "Add"}
                      </RemoveLink>
                    )}
                  </RowRight>
                </MemberRow>
              );
            })}
            {filteredMembers.length === 0 && (
              <Empty>Choose one or more subfactors to see matching contacts.</Empty>
            )}
          </MemberList>
        </Section>

        <Divider />

        <Section>
          <Actions>
            <Button
              color={actionType === "archive" ? "red" : actionType === "enable_survey" ? "green" : "blue"}
              disabled={archiving || selectedIds.length === 0}
              onClick={() => setShowConfirm(true)}
            >
              {actionType === "archive" 
                ? `Archive ${selectedIds.length} contacts` 
                : actionType === "enable_survey"
                ? `Enable Survey for ${selectedIds.length} contacts`
                : `Disable Survey for ${selectedIds.length} contacts`}
            </Button>
            {archiving && (
              <Button basic onClick={cancelArchive}>
                Cancel
              </Button>
            )}
          </Actions>
          {archiving && (
            <div style={{ marginTop: 10 }}>
              <Progress percent={percent} indicating>
                {progressIndex} / {total} {actionType === "archive" ? "archived" : "updated"}
              </Progress>
            </div>
          )}
          {!archiving && progressIndex > 0 && progressIndex === total && total > 0 && (
            <Message positive>
              {actionType === "archive" 
                ? `Archived ${progressIndex} contact${progressIndex === 1 ? "" : "s"}.`
                : actionType === "enable_survey"
                ? `Enabled survey for ${progressIndex} contact${progressIndex === 1 ? "" : "s"}.`
                : `Disabled survey for ${progressIndex} contact${progressIndex === 1 ? "" : "s"}.`}
            </Message>
          )}
        </Section>
      </Container>

      <Modal size="small" open={showConfirm} onClose={() => setShowConfirm(false)}>
        <Modal.Header>
          {actionType === "archive" 
            ? "Confirm Archive" 
            : actionType === "enable_survey"
            ? "Confirm Enable Survey"
            : "Confirm Disable Survey"}
        </Modal.Header>
        <Modal.Content>
          <p>
            You are about to {
              actionType === "archive" 
                ? "archive" 
                : actionType === "enable_survey"
                ? "enable survey participation for"
                : "disable survey participation for"
            } {selectedIds.length} contact{selectedIds.length === 1 ? "" : "s"}.
          </p>
          <p>
            {actionType === "archive" 
              ? "This will deactivate their access and move them to the archived list." 
              : actionType === "enable_survey"
              ? "This will allow these contacts to receive surveys. Make sure they are assigned to appropriate categories."
              : "This will prevent these contacts from receiving surveys. They will still have access if they have login enabled."}
            {" "}You can include an optional reason above.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowConfirm(false)}>Cancel</Button>
          <Button 
            color={actionType === "archive" ? "red" : actionType === "enable_survey" ? "green" : "blue"} 
            onClick={() => { setShowConfirm(false); startArchive(); }}
          >
            {actionType === "archive" 
              ? "Confirm Archive" 
              : actionType === "enable_survey"
              ? "Confirm Enable Survey"
              : "Confirm Disable Survey"}
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal size="large" open={showEmailModal} onClose={() => setShowEmailModal(false)}>
        <Modal.Header>Select Contacts by Email</Modal.Header>
        <Modal.Content>
          <div style={{ marginBottom: 20 }}>
            <p style={{ marginBottom: 10, fontSize: 14, color: '#666' }}>
              Paste a list of emails (comma, semicolon, or newline separated). Only exact matches will be selected.
            </p>
            <textarea
              placeholder="john@example.com, jane@example.com&#10;or&#10;john@example.com&#10;jane@example.com"
              value={emailList}
              onChange={(e) => {
                setEmailList(e.target.value);
                parseEmailList(e.target.value);
              }}
              style={{
                width: '100%',
                height: 120,
                padding: 12,
                border: '1px solid #ddd',
                borderRadius: 6,
                fontSize: 14,
                fontFamily: 'monospace',
                resize: 'vertical'
              }}
            />
          </div>
          
          {emailMatches.length > 0 && (
            <div>
              <h4 style={{ marginBottom: 10, fontSize: 16 }}>
                Email Matches ({emailMatches.filter(m => m.found).length} found, {emailMatches.filter(m => !m.found).length} not found)
              </h4>
              <div style={{ 
                maxHeight: 300, 
                overflowY: 'auto', 
                border: '1px solid #e9e9e9', 
                borderRadius: 6,
                padding: 10
              }}>
                {emailMatches.map((match, index) => (
                  <div key={index} style={{ 
                    padding: 8, 
                    marginBottom: 4, 
                    borderRadius: 4,
                    backgroundColor: match.found ? '#e6fbef' : '#fff2f0',
                    border: `1px solid ${match.found ? '#1a7f37' : '#ff4d4f'}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                        {match.email}
                      </span>
                      <span style={{ 
                        fontSize: 12, 
                        color: match.found ? '#1a7f37' : '#ff4d4f',
                        fontWeight: 500
                      }}>
                        {match.found ? `✓ ${match.member.first_name} ${match.member.last_name}` : '✗ Not found'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => {
            setShowEmailModal(false);
            setEmailList("");
            setEmailMatches([]);
          }}>
            Cancel
          </Button>
          <Button 
            color="blue" 
            onClick={applyEmailSelection}
            disabled={emailMatches.length === 0 || filteredMatchCount === 0}
          >
            Select {filteredMatchCount} Matching Contacts
          </Button>
          <Button
            basic
            onClick={applyEmailNonSelection}
            disabled={emailMatches.length === 0 || filteredNonMatchCount === 0}
          >
            Select {filteredNonMatchCount} Non-Matching Contacts
          </Button>
          <Button 
            color="green"
            onClick={applyEmailFilterInclude}
            disabled={emailMatches.length === 0 || filteredMatchCount === 0}
          >
            Filter to {filteredMatchCount} Matching Contacts
          </Button>
          <Button 
            color="orange"
            onClick={applyEmailFilterExclude}
            disabled={emailMatches.length === 0 || filteredNonMatchCount === 0}
          >
            Filter to {filteredNonMatchCount} Non-Matching Contacts
          </Button>
        </Modal.Actions>
      </Modal>

      <Dimmer active={loading}>
        <Loader />
      </Dimmer>
    </Dimmer.Dimmable>
  );
};

export default MassRemove;

const Container = styled.div`
  padding-left: 58px;
  margin-bottom: 50px;
`;

const TopTitle = styled.div`
  width: 100%;
  padding-left: 58px;
  margin-bottom: 20px;
  display: flex;
  align-items: baseline;
  margin-top: -10px;
`;

const Title = styled.div`
  font-family: "Poppins";
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 30px;
  color: #414141;
`;

const Sub = styled.div`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 12px;
  color: #7e7e7e;
  margin-left: 10px;
`;

const Section = styled.div`
  margin-top: 10px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 20px;
  width: 700px;
  justify-content: space-between;
`;

const RowLabel = styled.div`
  font-family: "Poppins";
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  width: 200px;
  align-self: center;
  color: #6d6d6d;
`;

const RowControl = styled.div`
  flex: 1;
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const Count = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
`;

const CountSub = styled.div`
  font-family: "Inter";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin-left: 8px;
`;

const MiniButton = styled.button`
  color: #7e7e7e;
  font-size: 12px;
  font-family: "Inter";
  cursor: pointer;
  border-radius: 20px;
  border: 1px solid #dfdfdf;
  background: white;
  padding: 6px 10px;
  margin-left: 6px;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const MemberList = styled.div`
  border: 1px solid #e9e9e9;
  border-radius: 10px;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #ffffff;
  &:nth-child(odd) {
    background: #fafafa;
  }
`;

const RowLeft = styled.div`
  display: flex;
  align-items: center;
`;

const RowRight = styled.div`
  display: flex;
  align-items: center;
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 15px;
`;

const MemberName = styled.div`
  font-family: "Poppins";
  font-size: 14px;
  color: #414141;
  margin-bottom: 2px;
`;

const ProfileLink = styled(Link)`
  color: #476dfa;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const MemberEmail = styled.div`
  font-family: "Inter";
  font-size: 12px;
  color: #7e7e7e;
`;

const AddedDate = styled.div`
  font-family: "Inter";
  font-size: 11px;
  color: #9E9FA1;
  display: flex;
  align-items: center;
  margin-left: 15px;
`;

const RemoveLink = styled.div`
  font-family: "Inter";
  font-size: 12px;
  color: #476dfa;
  cursor: pointer;
`;

const TagsGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
`;

const MiniTag = styled.div`
  font-family: "Inter";
  font-size: 10px;
  line-height: 12px;
  border-radius: 100px;
  padding: 4px 8px;
  border: 1px solid #dfdfdf;
  color: ${(p) => (p["data-active"] ? "#1a7f37" : "#7e7e7e")};
  background: ${(p) => (p["data-active"] ? "#e6fbef" : "#fafafa")};
`;

const StatusPill = styled.div`
  font-family: "Inter";
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  border-radius: 100px;
  padding: 6px 10px;
  color: #414141;
  background: ${(p) => (p["data-status"] === "done" ? "#e6fbef" : p["data-status"] === "processing" ? "#eef3ff" : "#f5f5f5")};
`;

const Empty = styled.div`
  font-family: "Poppins";
  font-size: 14px;
  color: #7e7e7e;
  padding: 16px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilterButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterButton = styled.button`
  font-family: "Inter";
  font-size: 13px;
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid ${(p) => (p["data-active"] ? "#476dfa" : "#dfdfdf")};
  background: ${(p) => (p["data-active"] ? "#476dfa" : "#ffffff")};
  color: ${(p) => (p["data-active"] ? "#ffffff" : "#7e7e7e")};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #476dfa;
    color: ${(p) => (p["data-active"] ? "#ffffff" : "#476dfa")};
  }
`;


